import type { FormEvent, ReactNode } from 'react'

import { useRef, useState } from 'react'
import { ArrowRight, Check, Loader2 } from 'lucide-react'

import { cn } from '@/lib/utils'

type Status = 'idle' | 'sending' | 'done' | 'error'

const fields = [
    { name: 'subject', label: 'Subject', type: 'text', required: true, span: 'sm:col-span-2', autoComplete: 'off' },
    { name: 'firstName', label: 'First Name', type: 'text', required: true, span: '', autoComplete: 'given-name' },
    { name: 'lastName', label: 'Last Name', type: 'text', required: true, span: '', autoComplete: 'family-name' },
    { name: 'email', label: 'Email', type: 'email', required: true, span: '', autoComplete: 'email' },
    { name: 'orderNumber', label: 'Order Number', type: 'text', required: false, span: '', autoComplete: 'off' }
] as const

export const SupportForm = (): ReactNode => {
    const [status, setStatus] = useState<Status>('idle')
    const [error, setError] = useState('')
    const form = useRef<HTMLFormElement | null>(null)

    const onSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
        event.preventDefault()

        const element = form.current

        if (!element) return

        const data = new FormData(element)

        const payload = {
            subject: String(data.get('subject') ?? ''),
            firstName: String(data.get('firstName') ?? ''),
            lastName: String(data.get('lastName') ?? ''),
            email: String(data.get('email') ?? ''),
            orderNumber: String(data.get('orderNumber') ?? ''),
            message: String(data.get('message') ?? '')
        }

        if (!payload.subject || !payload.firstName || !payload.lastName || !payload.email || !payload.message) {
            setStatus('error')
            setError('Please fill in every field marked with an asterisk.')
            return
        }

        setStatus('sending')
        setError('')

        try {
            const response = await fetch('/api/contact', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            })

            const result: { ok?: boolean; error?: string } = await response.json()

            if (!response.ok || !result.ok) {
                setStatus('error')
                setError(result.error ?? 'We could not send that just now. Please try again.')
                return
            }

            setStatus('done')
            element.reset()
        } catch {
            setStatus('error')
            setError('We could not send that just now. Please try again.')
        }
    }

    if (status === 'done') {
        return (
            <div
                data-support-form=''
                className='flex min-h-[320px] flex-col items-start justify-center gap-4 rounded-2xl border border-stone-line bg-shell p-8 md:p-12'
                role='status'
            >
                <span className='flex size-11 items-center justify-center rounded-full bg-sage text-ink'>
                    <Check className='size-5' strokeWidth={2} />
                </span>

                <h3 className='type-h3 text-ink'>
                    Message submitted
                </h3>

                <p className='type-copy max-w-[42ch] text-slate-soft'>
                    Thank you. Every message is read by a person here, and we answer within one working day.
                </p>

                <button
                    type='button'
                    onClick={() => setStatus('idle')}
                    className='type-small mt-2 cursor-pointer rounded-full border border-stone-line px-5 py-3 text-ink transition-colors duration-400 ease-nuvea hover:border-ink hover:bg-ink hover:text-cream'
                >
                    Send another message
                </button>
            </div>
        )
    }

    return (
        <form ref={form} onSubmit={onSubmit} noValidate data-support-form=''>
            <div className='grid grid-cols-1 gap-x-8 gap-y-2 sm:grid-cols-2'>
                {fields.map((field) => (
                    <div key={field.name} className={field.span}>
                        <label htmlFor={field.name} className='sr-only'>
                            {field.label}
                            {field.required ? ' (required)' : ''}
                        </label>

                        <input
                            id={field.name}
                            name={field.name}
                            type={field.type}
                            required={field.required}
                            autoComplete={field.autoComplete}
                            placeholder={`${field.label}${field.required ? '*' : ''}`}
                            className='field-line'
                        />
                    </div>
                ))}

                <div className='relative sm:col-span-2'>
                    <label htmlFor='message' className='sr-only'>
                        Your message (required)
                    </label>

                    <textarea
                        id='message'
                        name='message'
                        rows={4}
                        required
                        placeholder='Your Message*'
                        className='field-line resize-y'
                    />

                    <button
                        type='submit'
                        disabled={status === 'sending'}
                        aria-label='Send message'
                        className='absolute right-0 bottom-4 flex size-10 cursor-pointer items-center justify-center rounded-md bg-sage text-ink transition-[background-color,transform] duration-400 ease-nuvea hover:bg-ink hover:text-cream active:scale-95 disabled:cursor-wait disabled:opacity-60'
                    >
                        {status === 'sending' ? (
                            <Loader2 className='size-4 animate-spin' strokeWidth={1.8} />
                        ) : (
                            <ArrowRight className='size-4' strokeWidth={1.8} />
                        )}
                    </button>
                </div>
            </div>

            <p
                className={cn(
                    'type-micro mt-6',
                    status === 'error' ? 'text-clay' : 'text-slate-mute'
                )}
                role={status === 'error' ? 'alert' : undefined}
            >
                {status === 'error' ? error : '*Answered by a person, usually within one working day.'}
            </p>
        </form>
    )
}
